import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Typography,
  Upload,
  Button,
  Space,
  Alert,
  message,
  Tag,
  Table,
  Checkbox,
  Modal,
  Tooltip,
  Input,
  Select,
} from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  FileExcelOutlined,
  RollbackOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { UploadFile } from 'antd/es/upload/interface';
import { formazioneAutoAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

const { Title, Text, Paragraph } = Typography;

interface UdcRow {
  excel_row: number;
  pallet: string;
  barcode: string;
  qty: number;
}

interface SourcePlan {
  stock_id: number;
  qty: number;
  pool: { stock_code: string | null; document: string | null; document_item_id: number | null; warehouse: string | null; customer_order: string | null; supplier_order: string | null; supplier_order_id: number | null };
  rows: number;
  assigned_qty: number;
  barcodes: string[];
}

interface ProductPlan {
  code: string;
  product_id: number | null;
  document: string | null;
  excel_rows: number;
  excel_qty: number;
  rows_already: number;
  rows_conflict: number;
  rows_to_form: number;
  to_form_qty: number;
  db_to_form_qty: number;
  status: 'ok' | 'skipped' | 'needs_choice';
  reason: string | null;
  reason_code?: string | null;
  choice_reason: string | null;
  variant?: string | null;
  warnings: string[];
  sources: SourcePlan[];
  to_form: UdcRow[];
  proposal: Record<string, string[]>;
  assigned_rows_count: number;
  assigned_qty: number;
}

// sheet → codice prodotto → stock_id → barcode assegnati (scelta dell'utente)
type Assignments = Record<string, Record<string, Record<string, string[]>>>;

const assignmentOf = (a: Assignments, sheet: string, code: string): Record<string, string[]> =>
  (a[sheet] && a[sheet][code]) || {};

// Verifica una scelta: ogni riga di inventario non oltre i suoi pezzi, almeno un UDC assegnato
const assignmentStats = (p: ProductPlan, choice: Record<string, string[]>) => {
  const qtyOf: Record<string, number> = {};
  p.to_form.forEach((r) => { qtyOf[r.barcode] = r.qty; });
  let rows = 0; let pieces = 0; let valid = true;
  p.sources.forEach((s) => {
    const picked = choice[String(s.stock_id)] || [];
    const q = picked.reduce((acc, b) => acc + (qtyOf[b] || 0), 0);
    if (q > s.qty) valid = false;
    rows += picked.length; pieces += q;
  });
  if (rows === 0) valid = false;
  return { rows, pieces, valid };
};

// Spiegazioni in linguaggio semplice dei motivi per cui un prodotto non viene formato
// (chiave = reason_code restituito dal backend; il dettaglio tecnico resta in `reason`)
const REASON_EXPLANATIONS: Record<string, { title: string; text: string; action: string }> = {
  already_formed: {
    title: 'Già formato',
    text: 'Tutti gli UDC (pallet + collo) indicati nel file per questo prodotto sono già presenti in inventario per questa spedizione: la formazione è già stata fatta, a mano oppure con un caricamento precedente.',
    action: 'Nessuna azione necessaria. Se il file contiene dati diversi da quelli registrati, correggere gli UDC dall\'Inventario.',
  },
  choice_missing: {
    title: 'Scelta non effettuata',
    text: 'Per questo prodotto il programma non poteva decidere da solo da quali UDC prendere i pezzi di ogni riga di inventario e la scelta non è stata fatta prima di eseguire.',
    action: 'Ripetere la verifica, aprire il prodotto nell\'anteprima e assegnare gli UDC alle righe di inventario.',
  },
  variant_missing: {
    title: 'Codice con più varianti: mancano taglia e colore',
    text: 'In anagrafica questo codice esiste in più versioni (taglia e colore diversi) e il file non indica quale sia: il programma non può scegliere.',
    action: 'Aggiungere nel file le colonne "Taglia" e "Colore" con i codici dell\'anagrafica per le righe di questo prodotto.',
  },
  variant_not_found: {
    title: 'Taglia o colore non corrispondono',
    text: 'In anagrafica questo codice esiste in più versioni, ma la taglia e il colore scritti nel file non corrispondono a nessuna di esse (oppure corrispondono a più di una).',
    action: 'Correggere Taglia e Colore nel file usando i codici dell\'anagrafica (il dettaglio elenca le versioni esistenti).',
  },
  choice_invalid: {
    title: 'Scelta non valida',
    text: 'Gli UDC assegnati a una riga di inventario superano i pezzi disponibili su quella riga, oppure lo stesso UDC è stato assegnato a più righe.',
    action: 'Correggere l\'assegnazione nell\'anteprima e ripetere l\'esecuzione.',
  },
  nothing_to_form: {
    title: 'Nessuna merce da formare',
    text: 'In magazzino non c\'è merce di questo prodotto ancora senza pallet/collo per questa spedizione: è già stata formata, è già stata venduta oppure è entrata con i colli già assegnati.',
    action: 'Nessuna azione necessaria. Verificare in Inventario se si pensa che la merce dovrebbe esserci.',
  },
  qty_mismatch: {
    title: 'Le quantità non corrispondono',
    text: 'La somma dei pezzi indicati nel file per questo prodotto è diversa dai pezzi che in magazzino sono ancora da formare. Può succedere se parte della merce è già stata venduta o formata, oppure se il file riporta un totale sbagliato. Per sicurezza il programma non forma nulla finché i numeri non coincidono.',
    action: 'Controllare i pezzi nel file e in Inventario; correggere il file e ricaricarlo, oppure formare il prodotto manualmente.',
  },
  product_not_in_documents: {
    title: 'Prodotto non presente nei documenti di carico',
    text: 'Il codice prodotto indicato nel file non compare tra i prodotti dei documenti di carico di questa spedizione. Il codice potrebbe essere scritto in modo diverso, oppure la merce è arrivata con un\'altra spedizione.',
    action: 'Verificare il codice nel file e i prodotti del documento di carico della spedizione.',
  },
  no_document_in_warehouse: {
    title: 'Nessun documento di carico per questo magazzino',
    text: 'Il file indica un magazzino per il quale questa spedizione non ha documenti di carico elaborati, quindi non si sa da quale documento provenga la merce.',
    action: 'Verificare il codice magazzino nel file oppure elaborare il documento di carico mancante.',
  },
  warehouse_unknown: {
    title: 'Magazzino sconosciuto',
    text: 'Il codice magazzino scritto nel file non corrisponde a nessun magazzino registrato nel programma.',
    action: 'Correggere il codice magazzino nel file (colonna "magazzino") o registrare il magazzino in Anagrafica.',
  },
  warehouse_mixed: {
    title: 'Magazzini diversi nello stesso prodotto',
    text: 'Le righe del file per questo prodotto indicano magazzini diversi tra loro: non è possibile capire dove si trova la merce.',
    action: 'Uniformare il magazzino nelle righe del file per questo prodotto.',
  },
  ambiguous_document: {
    title: 'Documento di carico non individuabile',
    text: 'La merce di questo prodotto è entrata con più documenti di carico (per esempio uno doganale e uno nazionalizzato) e il file non indica il magazzino, quindi non si sa quale merce formare.',
    action: 'Compilare la colonna "magazzino" nel file per le righe di questo prodotto.',
  },
  rows_invalid: {
    title: 'Righe del file non valide',
    text: 'Alcune righe di questo prodotto hanno dati mancanti o sbagliati: quantità non intera o pari a zero, barcode mancante, oppure lo stesso barcode ripetuto nel file.',
    action: 'Correggere le righe indicate nel dettaglio e ricaricare il file.',
  },
  barcode_other_product: {
    title: 'Colli già usati per un altro prodotto',
    text: 'I barcode dei colli indicati nel file risultano già registrati in magazzino ma per un prodotto diverso: probabilmente c\'è un errore nel file.',
    action: 'Verificare i barcode nel file e la merce già registrata in Inventario.',
  },
  in_production_or_transit: {
    title: 'Merce non ancora arrivata',
    text: 'Parte di questa merce risulta ancora in produzione o in viaggio: si può formare solo la merce già entrata in magazzino.',
    action: 'Completare il carico della spedizione, poi ricaricare il file.',
  },
  locked: {
    title: 'Merce in uso da un altro utente',
    text: 'Un altro utente sta lavorando in questo momento su questa merce, per esempio una prenotazione in corso.',
    action: 'Riprovare tra qualche minuto.',
  },
  other_sheet: {
    title: 'Già trattato in un altro foglio',
    text: 'La stessa merce compare in più fogli del file: è stata considerata nel primo foglio e ignorata negli altri.',
    action: 'Nessuna azione necessaria, salvo verificare che il file non contenga fogli duplicati.',
  },
  non_available_reservations: {
    title: 'Prenotazioni da verificare',
    text: 'Su questa merce esistono prenotazioni di ordini cliente registrate su merce non ancora disponibile: la situazione va controllata prima di formare.',
    action: 'Verificare le prenotazioni del prodotto in Inventario.',
  },
  over_reserved: {
    title: 'Prenotazioni superiori alla merce',
    text: 'Gli ordini cliente hanno prenotato su questa merce più pezzi di quelli disponibili: la situazione va sistemata prima di formare.',
    action: 'Verificare le prenotazioni del prodotto in Inventario.',
  },
  not_partitionable: {
    title: 'Colli non ripartibili',
    text: 'La merce di questo prodotto è divisa in più righe di magazzino e un collo del file dovrebbe prendere pezzi da due righe diverse: il programma non lo fa in automatico.',
    action: 'Formare questo prodotto manualmente dall\'Inventario.',
  },
  changed_meanwhile: {
    title: 'Merce modificata durante la formazione',
    text: 'Tra la verifica e l\'esecuzione qualcuno ha modificato questa merce (vendita, prenotazione, spostamento).',
    action: 'Ripetere la verifica e l\'esecuzione.',
  },
};

const explain = (code?: string | null) => (code && REASON_EXPLANATIONS[code]) || {
  title: 'Prodotto non formato',
  text: 'Il programma non ha potuto formare questo prodotto in automatico.',
  action: 'Vedere il dettaglio; se necessario formare il prodotto manualmente dall\'Inventario.',
};

interface ShipmentCandidate {
  shipment_id: number;
  shipment_code: string;
  vehicle_plate: string | null;
  status: string | null;
  documents: string[];
  unformed_qty: number;
}

interface SheetPlan {
  sheet: string;
  plate: string | null;
  shipment_id: number | null;
  shipment_code: string | null;
  documents: string[];
  status: 'ok' | 'skipped' | 'error';
  message: string | null;
  rows_total: number;
  products: ProductPlan[];
  // targa condivisa da più spedizioni: candidate, proposta del programma, conferma obbligatoria
  shipment_candidates?: ShipmentCandidate[] | null;
  proposed_shipment_id?: number | null;
  shipment_confirmation_required?: boolean;
}

interface AnalysisResult {
  sheets: SheetPlan[];
  summary: {
    sheets_total: number;
    sheets_ok: number;
    products_ok: number;
    products_skipped: number;
    products_choice?: number;
    products_already_formed?: number;
    udc_rows: number;
    pieces: number;
  };
  applied?: { products: number; udc_rows: number; pieces: number };
  file?: { filename: string } | null;
  file_error?: string;
}

interface RunProduct {
  code: string;
  document: string | null;
  sources: number;
  children: number;
  blocks?: string[];
}

interface FormazioneRun {
  id: number;
  execution_id: string;
  created_at: string | null;
  created_by: string | null;
  filename: string | null;
  sheet_name: string;
  plate: string | null;
  shipment_code: string | null;
  products_count: number;
  udc_rows: number;
  pieces: number;
  status: 'applied' | 'rolled_back';
  rolled_back_at: string | null;
  rolled_back_by: string | null;
  rollback_notes: string | null;
  can_rollback: boolean;
  blocks: string[];
  products: RunProduct[];
}

const ACCEPT = '.xls,.xlsx,.ods';

const statusTag = (status: string) => {
  if (status === 'ok') return <Tag color="green">OK</Tag>;
  if (status === 'error') return <Tag color="red">ERRORE</Tag>;
  return <Tag color="orange">SALTATO</Tag>;
};

const Formazione: React.FC = () => {
  const { canWrite } = useAuth();
  const canExecute = canWrite('formazione-auto');

  const [file, setFile] = useState<File | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [validating, setValidating] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [selectedSheets, setSelectedSheets] = useState<string[]>([]);
  const [lastExecution, setLastExecution] = useState<AnalysisResult | null>(null);
  const [skipModalSheet, setSkipModalSheet] = useState<SheetPlan | null>(null);
  const [assignments, setAssignments] = useState<Assignments>({});
  // targhe condivise da più spedizioni: spedizione scelta per foglio e conferma esplicita
  const [shipmentChoices, setShipmentChoices] = useState<Record<string, number>>({});
  const [shipmentConfirmed, setShipmentConfirmed] = useState<Record<string, boolean>>({});
  const [runs, setRuns] = useState<FormazioneRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState<number | null>(null);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const data = await formazioneAutoAPI.listRuns({ limit: 50 });
      setRuns(data?.items || []);
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nel caricamento delle esecuzioni');
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const handleRollback = (run: FormazioneRun) => {
    let notes = '';
    Modal.confirm({
      title: `Annullare la formazione del container ${run.plate || run.sheet_name}?`,
      content: (
        <div>
          <p>
            Spedizione <b>{run.shipment_code || '—'}</b>, {run.products_count} prodotti, {run.udc_rows} righe UDC, {run.pieces} pz
            (eseguita il {run.created_at ? dayjs(run.created_at).format('DD/MM/YYYY HH:mm') : '—'} da {run.created_by || '—'}).
          </p>
          <p>
            Le righe UDC create verranno eliminate, le giacenze di origine torneranno com'erano (senza UDC, quantità intera),
            prenotazioni e coperture verranno riportate sulle giacenze di origine e i movimenti della formazione rimossi.
          </p>
          <Input.TextArea rows={2} placeholder="Motivo (facoltativo)" onChange={(e) => { notes = e.target.value; }} />
        </div>
      ),
      okText: 'Esegui Rollback',
      okButtonProps: { danger: true },
      cancelText: 'Annulla',
      onOk: async () => {
        setRollingBack(run.id);
        try {
          const res = await formazioneAutoAPI.rollbackRun(run.id, notes.trim() || undefined);
          message.success(res?.message || 'Formazione annullata', 8);
          loadRuns();
        } catch (error: any) {
          message.error(error.response?.data?.detail || 'Errore durante il rollback della formazione', 10);
        } finally {
          setRollingBack(null);
        }
      },
    });
  };

  const reset = () => {
    setResult(null);
    setSelectedSheets([]);
    setShipmentChoices({});
    setShipmentConfirmed({});
  };

  const handleSelect = (f: File) => {
    setFile(f);
    setFileList([{ uid: 'file', name: f.name, status: 'done' } as UploadFile]);
    reset();
    setLastExecution(null);
  };

  const handleRemove = () => {
    setFile(null);
    setFileList([]);
    reset();
  };

  // changedSheet: ri-verifica dopo il cambio di spedizione di un foglio (le scelte
  // degli altri fogli restano quelle già fatte)
  const runValidate = async (choices: Record<string, number>, changedSheet?: string) => {
    if (!file) return;
    setValidating(true);
    if (!changedSheet) setResult(null);
    setLastExecution(null);
    try {
      const data: AnalysisResult = await formazioneAutoAPI.validate(file, choices);
      const prevResult = result;
      setResult(data);
      const okSheets = data.sheets.filter((s) => s.status === 'ok').map((s) => s.sheet);
      setSelectedSheets((prev) => changedSheet && prevResult
        ? okSheets.filter((n) => n === changedSheet || prev.includes(n))
        : okSheets);
      // spedizione proposta dal programma come scelta iniziale (da confermare)
      const nextChoices: Record<string, number> = { ...choices };
      data.sheets.forEach((s) => {
        if (s.shipment_confirmation_required && nextChoices[s.sheet] === undefined && s.proposed_shipment_id) {
          nextChoices[s.sheet] = s.proposed_shipment_id;
        }
      });
      setShipmentChoices(nextChoices);
      if (!changedSheet) setShipmentConfirmed({});
      // proposte del programma come punto di partenza per i prodotti che richiedono la scelta
      const initial: Assignments = {};
      data.sheets.forEach((s) => {
        s.products.filter((p) => p.status === 'needs_choice').forEach((p) => {
          (initial[s.sheet] = initial[s.sheet] || {})[p.code] = { ...(p.proposal || {}) };
        });
      });
      setAssignments((prev) => {
        if (!changedSheet) return initial;
        const merged: Assignments = { ...initial };
        Object.keys(prev).forEach((sh) => { if (sh !== changedSheet && merged[sh]) merged[sh] = prev[sh]; });
        return merged;
      });
      if (changedSheet) return;
      const choice = data.summary.products_choice || 0;
      if (data.summary.products_ok > 0 || choice > 0) {
        message.success(`Verifica completata: ${data.summary.products_ok} prodotti formabili in automatico (${data.summary.udc_rows} UDC, ${data.summary.pieces} pz)` +
          (choice > 0 ? `, ${choice} da assegnare manualmente.` : '.'), 6);
      } else {
        message.warning('Verifica completata: nessun prodotto formabile con questo file.');
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore durante la verifica del file');
    } finally {
      setValidating(false);
    }
  };

  const handleValidate = () => runValidate({});

  // cambio della spedizione di un foglio: la conferma va ridata e l'anteprima ricalcolata
  const handleShipmentChange = (sheet: string, shipmentId: number) => {
    const next = { ...shipmentChoices, [sheet]: shipmentId };
    setShipmentConfirmed((prev) => ({ ...prev, [sheet]: false }));
    runValidate(next, sheet);
  };

  const needsShipmentConfirmation = (s: SheetPlan) => !!s.shipment_confirmation_required && s.status === 'ok';

  const okSelected = useMemo(() => {
    if (!result) return { products: 0, rows: 0, pieces: 0, invalid: 0 };
    let products = 0; let rows = 0; let pieces = 0; let invalid = 0;
    result.sheets.filter((s) => s.status === 'ok' && selectedSheets.includes(s.sheet)).forEach((s) => {
      s.products.forEach((p) => {
        if (p.status === 'ok') {
          products += 1; rows += p.assigned_rows_count; pieces += p.assigned_qty;
        } else if (p.status === 'needs_choice') {
          const st = assignmentStats(p, assignmentOf(assignments, s.sheet, p.code));
          if (st.valid) { products += 1; rows += st.rows; pieces += st.pieces; } else { invalid += 1; }
        }
      });
    });
    return { products, rows, pieces, invalid };
  }, [result, selectedSheets, assignments]);

  const setChoice = (sheet: string, code: string, stockId: number, barcodes: string[]) => {
    setAssignments((prev) => ({
      ...prev,
      [sheet]: { ...(prev[sheet] || {}), [code]: { ...assignmentOf(prev, sheet, code), [String(stockId)]: barcodes } },
    }));
  };

  const handleExecute = () => {
    if (!file || !result || okSelected.products === 0) return;
    if (okSelected.invalid > 0) {
      message.warning(`${okSelected.invalid} prodotti hanno un'assegnazione non valida (pezzi oltre la riga o nessun UDC): correggila o deseleziona il foglio.`);
      return;
    }
    // targhe condivise da più spedizioni: la spedizione va confermata esplicitamente
    const toConfirm = result.sheets.filter((s) => selectedSheets.includes(s.sheet) && needsShipmentConfirmation(s));
    const unconfirmed = toConfirm.filter((s) => !shipmentConfirmed[s.sheet]);
    if (unconfirmed.length) {
      message.warning(`Conferma la spedizione da formare per: ${unconfirmed.map((s) => s.sheet).join(', ')} (la targa è usata da più spedizioni), oppure deseleziona il foglio.`, 8);
      return;
    }
    const confirmedShipments: Record<string, number> = {};
    toConfirm.forEach((s) => { if (s.shipment_id) confirmedShipments[s.sheet] = s.shipment_id; });
    // solo le scelte dei fogli selezionati e dei prodotti che le richiedono
    const chosen: Assignments = {};
    result.sheets.filter((s) => selectedSheets.includes(s.sheet)).forEach((s) => {
      s.products.filter((p) => p.status === 'needs_choice').forEach((p) => {
        const c = assignmentOf(assignments, s.sheet, p.code);
        if (Object.keys(c).length) (chosen[s.sheet] = chosen[s.sheet] || {})[p.code] = c;
      });
    });
    Modal.confirm({
      title: 'Eseguire la formazione?',
      content: (
        <div>
          <p>
            Verranno suddivise le giacenze di <b>{okSelected.products}</b> prodotti in <b>{okSelected.rows}</b> righe UDC
            ({okSelected.pieces} pz) sui fogli selezionati: {selectedSheets.join(', ')}.
          </p>
          {toConfirm.length > 0 && (
            <p>
              Spedizioni confermate per le targhe usate da più spedizioni:{' '}
              {toConfirm.map((s) => `${s.sheet} → ${s.shipment_code}`).join(', ')}.
            </p>
          )}
          <p>Prenotazioni e coperture seguono la merce. Al termine verrà scaricato il file di esito.</p>
        </div>
      ),
      okText: 'Esegui Formazione',
      cancelText: 'Annulla',
      onOk: async () => {
        setExecuting(true);
        try {
          const data: AnalysisResult = await formazioneAutoAPI.execute(file, selectedSheets, chosen, confirmedShipments);
          setLastExecution(data);
          setResult(null);
          setSelectedSheets([]);
          setShipmentChoices({});
          setShipmentConfirmed({});
          loadRuns();
          const a = data.applied || { products: 0, udc_rows: 0, pieces: 0 };
          message.success(`Formazione eseguita: ${a.products} prodotti, ${a.udc_rows} righe UDC, ${a.pieces} pz.` +
            (data.file ? ` File di esito scaricato: ${data.file.filename}` : ''), 8);
          if (data.file_error) message.warning(data.file_error, 8);
        } catch (error: any) {
          message.error(error.response?.data?.detail || 'Errore durante la formazione');
        } finally {
          setExecuting(false);
        }
      },
    });
  };

  const productColumns = (sheet: string) => [
    {
      title: 'Codice prodotto',
      dataIndex: 'code',
      key: 'code',
      render: (v: string, p: ProductPlan) => (
        <Space direction="vertical" size={0}>
          <Text strong>{p.variant ? v.split(' ')[0] : v}</Text>
          {p.variant && <Text type="secondary">taglia / colore: {p.variant}</Text>}
        </Space>
      ),
    },
    { title: 'Documento di carico', dataIndex: 'document', key: 'document', render: (v: string | null) => v || '—' },
    {
      title: <Tooltip title="Quanti UDC (pallet + collo) il file del magazzino indica per questo prodotto in questo container">Nel file del magazzino</Tooltip>,
      key: 'udc',
      render: (_: unknown, p: ProductPlan) => (
        <Space direction="vertical" size={0}>
          <span>{p.excel_rows} UDC ({p.excel_qty} pz)</span>
          {p.rows_already > 0 && <Text type="secondary">di cui {p.rows_already} già presenti in inventario</Text>}
          {p.rows_conflict > 0 && <Text type="danger">di cui {p.rows_conflict} con barcode di un altro prodotto</Text>}
        </Space>
      ),
    },
    {
      title: <Tooltip title="UDC del file che ancora non esistono in inventario e che la formazione deve creare">UDC ancora da creare</Tooltip>,
      key: 'to_form',
      render: (_: unknown, p: ProductPlan) => p.rows_to_form ? `${p.rows_to_form} UDC (${p.to_form_qty} pz)` : 'nessuno',
    },
    {
      title: <Tooltip title="Pezzi di questo prodotto che in inventario sono ancora senza UDC: sono quelli con cui si creano gli UDC del file">In inventario senza UDC</Tooltip>,
      key: 'db_to_form',
      render: (_: unknown, p: ProductPlan) => {
        if (!p.sources.length) return `${p.db_to_form_qty} pz`;
        const choice = p.status === 'needs_choice' ? assignmentOf(assignments, sheet, p.code) : null;
        return (
          <Space direction="vertical" size={0}>
            <span>{p.db_to_form_qty} pz su {p.sources.length} {p.sources.length === 1 ? 'riga' : 'righe'}</span>
            {p.sources.map((s) => {
              const picked = choice ? (choice[String(s.stock_id)] || []).length : s.rows;
              return <Text key={s.stock_id} type="secondary">riga #{s.stock_id}: {s.qty} pz → {picked} UDC</Text>;
            })}
          </Space>
        );
      },
    },
    {
      title: 'Cosa farà il programma',
      key: 'status',
      render: (_: unknown, p: ProductPlan) => {
        if (p.status === 'needs_choice') {
          const st = assignmentStats(p, assignmentOf(assignments, sheet, p.code));
          return (
            <Space direction="vertical" size={0}>
              <span><Tag color={st.valid ? 'blue' : 'red'}>SERVE LA TUA SCELTA</Tag></span>
              <Text type="secondary">Il programma non può decidere da solo: {p.choice_reason}.</Text>
              <Text type={st.valid ? undefined : 'danger'}>
                {st.valid
                  ? `Con la scelta attuale verranno creati ${st.rows} UDC (${st.pieces} pz).`
                  : 'Scelta incompleta o non valida: apri la riga qui sotto e assegna gli UDC.'}
              </Text>
              {p.warnings.map((w, i) => <Text key={i} type="warning">⚠ {w}</Text>)}
            </Space>
          );
        }
        if (p.status === 'ok') {
          return (
            <Space direction="vertical" size={0}>
              <span><Tag color="green">VERRÀ FORMATO</Tag> <Text type="secondary">crea {p.assigned_rows_count} UDC ({p.assigned_qty} pz) dai pezzi senza UDC</Text></span>
              {p.assigned_rows_count < p.rows_to_form && (
                <Text type="warning">⚠ {p.rows_to_form - p.assigned_rows_count} UDC del file non verranno creati (pezzi insufficienti o esclusi dalla scelta)</Text>
              )}
              {p.warnings.map((w, i) => <Text key={i} type="warning">⚠ {w}</Text>)}
            </Space>
          );
        }
        return (
          <Space direction="vertical" size={0}>
            <span><Tag color="orange">NON VIENE FORMATO</Tag> <Text type="secondary">{explain(p.reason_code).title}</Text></span>
            {p.reason && <Text type="secondary">{p.reason}</Text>}
            {p.warnings.map((w, i) => <Text key={i} type="warning">⚠ {w}</Text>)}
          </Space>
        );
      },
    },
  ];

  // Editor della scelta: per ogni riga di inventario senza UDC, gli UDC del file da assegnarle
  const renderChoiceEditor = (sheet: string, p: ProductPlan) => {
    const choice = assignmentOf(assignments, sheet, p.code);
    const qtyOf: Record<string, number> = {};
    p.to_form.forEach((r) => { qtyOf[r.barcode] = r.qty; });
    const usedElsewhere = (stockId: number) => new Set(
      Object.entries(choice).filter(([sid]) => sid !== String(stockId)).flatMap(([, b]) => b)
    );
    const assignedAll = new Set(Object.values(choice).flat());
    const unassigned = p.to_form.filter((r) => !assignedAll.has(r.barcode));
    const toFormQty = p.to_form.reduce((a, r) => a + r.qty, 0);
    return (
      <div style={{ padding: '4px 0 8px 24px' }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 10 }}
          message="Cosa devi fare"
          description={
            <div>
              <div><b>In inventario</b> questo prodotto ha {p.sources.length} {p.sources.length === 1 ? 'riga' : 'righe'} di merce ancora senza UDC, per un totale di {p.db_to_form_qty} pz (elencate qui sotto con i loro dati).</div>
              <div><b>Nel file del magazzino</b> ci sono {p.to_form.length} UDC ancora da creare, per un totale di {toFormQty} pz.</div>
              <div style={{ marginTop: 4 }}>
                Per ogni riga di inventario scegli <b>quali UDC del file riempire con i suoi pezzi</b>. I pezzi degli UDC scelti non possono
                superare i pezzi della riga; ogni UDC può essere riempito da una sola riga (un UDC non si divide mai).
                Gli UDC che lasci senza riga <b>non verranno creati</b> e i pezzi resteranno in inventario senza UDC.
              </div>
            </div>
          }
        />
        {p.sources.map((s) => {
          const picked = choice[String(s.stock_id)] || [];
          const q = picked.reduce((acc, b) => acc + (qtyOf[b] || 0), 0);
          const blocked = usedElsewhere(s.stock_id);
          return (
            <div key={s.stock_id} style={{ marginBottom: 10, padding: 8, border: '1px solid #f0f0f0', borderRadius: 4 }}>
              <div>
                <Text strong>Riga di inventario #{s.stock_id}: {s.qty} pz senza UDC</Text>
              </div>
              <div>
                <Text type="secondary">
                  partita {s.pool.stock_code || '—'} · documento {s.pool.document || '—'}{s.pool.document_item_id ? ` (riga #${s.pool.document_item_id})` : ''} · magazzino {s.pool.warehouse || '—'}
                  {' '}· ordine cliente {s.pool.customer_order || '—'} · ordine fornitore {s.pool.supplier_order || '—'}
                </Text>
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%', marginTop: 4 }}
                placeholder="Scegli gli UDC del file da riempire con i pezzi di questa riga"
                value={picked}
                onChange={(vals: string[]) => setChoice(sheet, p.code, s.stock_id, vals)}
                optionFilterProp="label"
                options={p.to_form.filter((r) => !blocked.has(r.barcode)).map((r) => ({
                  value: r.barcode, label: `pallet ${r.pallet || '—'} / collo ${r.barcode} — ${r.qty} pz`,
                }))}
              />
              <Text type={q > s.qty ? 'danger' : 'secondary'}>
                {q > s.qty
                  ? `Troppi pezzi: gli UDC scelti valgono ${q} pz ma la riga ne ha solo ${s.qty}.`
                  : `UDC scelti: ${picked.length} per ${q} pz su ${s.qty} disponibili${s.qty - q > 0 ? ` (${s.qty - q} pz resteranno senza UDC)` : ''}.`}
              </Text>
            </div>
          );
        })}
        <Text type={unassigned.length ? 'warning' : 'secondary'}>
          {unassigned.length
            ? `UDC del file non ancora assegnati a nessuna riga: ${unassigned.length} (${unassigned.reduce((a, r) => a + r.qty, 0)} pz) — non verranno creati.`
            : 'Tutti gli UDC del file sono stati assegnati.'}
        </Text>
      </div>
    );
  };

  // Targa usata da più spedizioni: il programma propone quella con merce da formare,
  // l'utente può sceglierne un'altra e deve comunque confermarla prima dell'esecuzione
  const renderShipmentChoice = (s: SheetPlan, selectable: boolean) => {
    const candidates = s.shipment_candidates || [];
    if (!selectable || !candidates.length) return s.shipment_code || '—';
    const value = shipmentChoices[s.sheet] ?? s.shipment_id ?? undefined;
    return (
      <Space direction="vertical" size={2} style={{ minWidth: 260 }}>
        <Tooltip title={`La targa ${s.plate || ''} è usata da ${candidates.length} spedizioni: scegli quella da formare`}>
          <Tag color="gold">targa usata da {candidates.length} spedizioni</Tag>
        </Tooltip>
        <Select
          size="small"
          style={{ width: '100%' }}
          placeholder="Scegli la spedizione"
          value={value}
          disabled={validating || executing}
          onChange={(v: number) => handleShipmentChange(s.sheet, v)}
          options={candidates.map((c) => ({
            value: c.shipment_id,
            label: `${c.shipment_code} — ${c.status || '—'} — ${c.unformed_qty} pz da formare`
              + (c.shipment_id === s.proposed_shipment_id ? ' (proposta)' : ''),
            title: c.documents.length ? `Documenti: ${c.documents.join(', ')}` : 'Nessun documento di carico elaborato',
          }))}
        />
        {s.proposed_shipment_id && value === s.proposed_shipment_id && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Proposta: è l'unica spedizione con merce senza UDC sui documenti di carico elaborati.
          </Text>
        )}
        {needsShipmentConfirmation(s) && (
          <Checkbox
            checked={!!shipmentConfirmed[s.sheet]}
            disabled={validating || executing}
            onChange={(e) => setShipmentConfirmed((prev) => ({ ...prev, [s.sheet]: e.target.checked }))}
          >
            <Text strong>Confermo la spedizione {s.shipment_code}</Text>
          </Checkbox>
        )}
      </Space>
    );
  };

  const sheetColumns = (selectable: boolean) => [
    {
      title: 'Applica',
      key: 'select',
      width: 70,
      render: (_: unknown, s: SheetPlan) => (
        <Checkbox
          disabled={s.status !== 'ok'}
          checked={selectedSheets.includes(s.sheet)}
          onChange={(e) => setSelectedSheets((prev) => e.target.checked ? [...prev, s.sheet] : prev.filter((x) => x !== s.sheet))}
        />
      ),
    },
    { title: 'Foglio', dataIndex: 'sheet', key: 'sheet', render: (v: string) => <Text strong>{v}</Text> },
    { title: 'Targa / Container', dataIndex: 'plate', key: 'plate', render: (v: string | null) => v || '—' },
    { title: 'Spedizione', key: 'shipment_code', render: (_: unknown, s: SheetPlan) => renderShipmentChoice(s, selectable) },
    { title: 'Documenti', dataIndex: 'documents', key: 'documents', render: (v: string[]) => v.length ? v.join(', ') : '—' },
    { title: 'Righe', dataIndex: 'rows_total', key: 'rows_total', align: 'right' as const },
    {
      title: 'Prodotti',
      key: 'products',
      render: (_: unknown, s: SheetPlan) => {
        const ok = s.products.filter((p) => p.status === 'ok').length;
        const choice = s.products.filter((p) => p.status === 'needs_choice').length;
        const skipped = s.products.length - ok - choice;
        return (
          <span>
            <Tooltip title="Prodotti che verranno formati in automatico"><Tag color="green">{ok} automatici</Tag></Tooltip>
            {choice > 0 && <Tooltip title="Prodotti per cui devi scegliere tu gli UDC (apri la riga del foglio)"><Tag color="blue">{choice} con scelta</Tag></Tooltip>}
            {skipped > 0 ? (
              <Tooltip title="Clicca per vedere perché questi prodotti non vengono formati">
                <Tag
                  color="orange"
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={(e) => { e.stopPropagation(); setSkipModalSheet(s); }}
                >
                  {skipped} non formati
                </Tag>
              </Tooltip>
            ) : <Tag color="orange">0 non formati</Tag>}
          </span>
        );
      },
    },
    {
      title: 'Esito',
      key: 'status',
      render: (_: unknown, s: SheetPlan) => (
        <Tooltip title={s.message || ''}>
          <span>{statusTag(s.status)} {s.message && <Text type="secondary">{s.message}</Text>}</span>
        </Tooltip>
      ),
    },
  ];

  const runColumns = [
    { title: 'Data', dataIndex: 'created_at', key: 'created_at', render: (v: string | null) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—' },
    { title: 'Utente', dataIndex: 'created_by', key: 'created_by', render: (v: string | null) => v || '—' },
    { title: 'File', dataIndex: 'filename', key: 'filename', render: (v: string | null) => v || '—' },
    { title: 'Container / Foglio', key: 'plate', render: (_: unknown, r: FormazioneRun) => <Text strong>{r.plate || r.sheet_name}</Text> },
    { title: 'Spedizione', dataIndex: 'shipment_code', key: 'shipment_code', render: (v: string | null) => v || '—' },
    { title: 'Prodotti', dataIndex: 'products_count', key: 'products_count', align: 'right' as const },
    { title: 'Righe UDC', dataIndex: 'udc_rows', key: 'udc_rows', align: 'right' as const },
    { title: 'Pezzi', dataIndex: 'pieces', key: 'pieces', align: 'right' as const },
    {
      title: 'Stato',
      key: 'status',
      render: (_: unknown, r: FormazioneRun) => r.status === 'rolled_back'
        ? (
          <Tooltip title={`Ripristinata il ${r.rolled_back_at ? dayjs(r.rolled_back_at).format('DD/MM/YYYY HH:mm') : '—'} da ${r.rolled_back_by || '—'}${r.rollback_notes ? ` — ${r.rollback_notes}` : ''}`}>
            <Tag color="purple">RIPRISTINATA</Tag>
          </Tooltip>
        )
        : <Tag color="blue">APPLICATA</Tag>,
    },
    {
      title: 'Ripristino',
      key: 'rollback',
      render: (_: unknown, r: FormazioneRun) => {
        if (r.status === 'rolled_back') return '—';
        if (r.can_rollback) {
          return (
            <Space>
              <Tag color="green">ripristinabile</Tag>
              {canExecute && (
                <Button size="small" danger icon={<RollbackOutlined />} loading={rollingBack === r.id} onClick={() => handleRollback(r)}>
                  Rollback
                </Button>
              )}
            </Space>
          );
        }
        return (
          <Tooltip title={<div>{r.blocks.map((b, i) => <div key={i}>{b}</div>)}</div>}>
            <Tag color="orange">bloccato: modifiche successive</Tag>
          </Tooltip>
        );
      },
    },
  ];

  const runProductColumns = [
    { title: 'Prodotto', dataIndex: 'code', key: 'code', render: (v: string) => <Text strong>{v}</Text> },
    { title: 'Documento', dataIndex: 'document', key: 'document', render: (v: string | null) => v || '—' },
    { title: 'Giacenze origine', dataIndex: 'sources', key: 'sources', align: 'right' as const },
    { title: 'Righe UDC create', dataIndex: 'children', key: 'children', align: 'right' as const },
    {
      title: 'Integrità',
      key: 'blocks',
      render: (_: unknown, p: RunProduct) => (p.blocks && p.blocks.length)
        ? <Space direction="vertical" size={0}>{p.blocks.map((b, i) => <Text key={i} type="warning">⚠ {b}</Text>)}</Space>
        : <Tag color="green">intatta</Tag>,
    },
  ];

  const renderSkippedModal = () => {
    if (!skipModalSheet) return null;
    const skipped = skipModalSheet.products.filter((p) => p.status !== 'ok');
    const groups: Record<string, ProductPlan[]> = {};
    skipped.forEach((p) => {
      const key = p.reason_code || 'unknown';
      (groups[key] = groups[key] || []).push(p);
    });
    return (
      <Modal
        open
        title={`Prodotti non formati — ${skipModalSheet.plate || skipModalSheet.sheet}`}
        onCancel={() => setSkipModalSheet(null)}
        footer={<Button onClick={() => setSkipModalSheet(null)}>Chiudi</Button>}
        width={860}
      >
        <Paragraph type="secondary">
          {skipped.length} prodotti di questo container non verranno formati. Per ogni motivo trovi una spiegazione,
          cosa puoi fare e l'elenco dei prodotti interessati (con il dettaglio tecnico in grigio).
        </Paragraph>
        {Object.entries(groups).map(([code, items]) => {
          const ex = explain(code);
          return (
            <Card key={code} size="small" style={{ marginBottom: 12 }} title={<span><Tag color="orange">{items.length}</Tag> {ex.title}</span>}>
              <p style={{ marginBottom: 6 }}>{ex.text}</p>
              <p style={{ marginBottom: 8 }}><Text strong>Cosa fare: </Text>{ex.action}</p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {items.map((p) => (
                  <li key={p.code}>
                    <Text strong>{p.code}</Text>
                    {' '}— {p.excel_rows} colli nel file, {p.excel_qty} pz
                    {p.db_to_form_qty > 0 && ` (da formare in magazzino: ${p.db_to_form_qty} pz)`}
                    {p.reason && <div><Text type="secondary" style={{ fontSize: 12 }}>{p.reason}</Text></div>}
                  </li>
                ))}
              </ul>
            </Card>
          );
        })}
      </Modal>
    );
  };

  const renderSheets = (data: AnalysisResult, selectable: boolean) => (
    <Table
      rowKey="sheet"
      columns={selectable ? sheetColumns(true) : sheetColumns(false).slice(1)}
      dataSource={data.sheets}
      size="small"
      pagination={false}
      expandable={{
        expandedRowRender: (s: SheetPlan) => (
          <Table
            rowKey="code"
            columns={productColumns(s.sheet)}
            dataSource={s.products}
            size="small"
            pagination={false}
            expandable={selectable ? {
              expandedRowRender: (p: ProductPlan) => renderChoiceEditor(s.sheet, p),
              rowExpandable: (p: ProductPlan) => p.status === 'needs_choice',
              defaultExpandAllRows: true,
            } : undefined}
          />
        ),
        rowExpandable: (s: SheetPlan) => s.products.length > 0,
      }}
    />
  );

  return (
    <div>
      <Title level={3}>
        <FileExcelOutlined /> Formazione
      </Title>
      <Paragraph type="secondary">
        Carica il file inviato dal magazzino (un foglio per targa/container, una riga per collo con
        <Text code>Codice Prodotto Cliente</Text>, <Text code>Barcode</Text> = UDC COLLO, <Text code>Codice Pedana</Text> = UDC PALLET,
        <Text code>Quantità Prodotti</Text>, <Text code>magazzino</Text>). La targa individua la spedizione e i suoi documenti di carico
        elaborati; le giacenze senza UDC di ogni prodotto vengono suddivise nelle righe UDC del file. Un prodotto viene saltato se è
        già formato, se la somma del file non coincide con i pezzi da formare o se il documento non è individuabile.
        Al termine viene scaricata una copia del file con le righe <Tag color="green">formate</Tag> in verde,
        le <Tag color="red">saltate</Tag> in rosso e la colonna "Esito Formazione".
      </Paragraph>

      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Upload
            accept={ACCEPT}
            maxCount={1}
            fileList={fileList}
            beforeUpload={(f) => { handleSelect(f as unknown as File); return false; }}
            onRemove={handleRemove}
          >
            <Button icon={<UploadOutlined />}>Seleziona file (.xls / .xlsx / .ods)</Button>
          </Upload>
          <Space>
            <Button icon={<CheckCircleOutlined />} onClick={handleValidate} loading={validating} disabled={!file}>
              Verifica
            </Button>
            {canExecute && (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleExecute}
                loading={executing}
                disabled={!result || okSelected.products === 0}
              >
                Esegui Formazione
              </Button>
            )}
            {result && (
              <Text type="secondary">
                Selezionati: {okSelected.products} prodotti, {okSelected.rows} righe UDC, {okSelected.pieces} pz
              </Text>
            )}
          </Space>
        </Space>
      </Card>

      {result && (
        <Card style={{ marginTop: 16 }} title="Anteprima">
          <Alert
            type={result.summary.products_ok > 0 ? 'info' : 'warning'}
            showIcon
            style={{ marginBottom: 12 }}
            message={`${result.summary.sheets_ok} container su ${result.summary.sheets_total} pronti — prodotti: ${result.summary.products_ok} formati in automatico, ${result.summary.products_choice || 0} con scelta da fare, ${result.summary.products_skipped} non formati (di cui ${result.summary.products_already_formed || 0} già formati in precedenza). In automatico verranno creati ${result.summary.udc_rows} UDC per ${result.summary.pieces} pz.`}
          />
          {renderSheets(result, true)}
        </Card>
      )}

      {lastExecution && (
        <Card style={{ marginTop: 16 }} title="Esito dell'ultima formazione">
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Formati ${lastExecution.applied?.products ?? 0} prodotti in ${lastExecution.applied?.udc_rows ?? 0} righe UDC (${lastExecution.applied?.pieces ?? 0} pz)`}
            description={lastExecution.file ? `File di esito: ${lastExecution.file.filename}` : lastExecution.file_error}
          />
          {renderSheets(lastExecution, false)}
        </Card>
      )}

      {renderSkippedModal()}

      <Card
        style={{ marginTop: 16 }}
        title="Esecuzioni (una per container)"
        extra={<Button icon={<ReloadOutlined />} onClick={loadRuns} loading={runsLoading}>Aggiorna</Button>}
      >
        <Paragraph type="secondary">
          Ogni esecuzione può essere annullata con il <b>Rollback</b> finché nessuna delle righe toccate è stata modificata
          (vendite, prenotazioni, trasferimenti, cambi UDC, rettifiche). Se una sola giacenza del container è cambiata il rollback è bloccato.
        </Paragraph>
        <Table
          rowKey="id"
          columns={runColumns}
          dataSource={runs}
          loading={runsLoading}
          size="small"
          pagination={{ pageSize: 10 }}
          expandable={{
            expandedRowRender: (r: FormazioneRun) => (
              <Table rowKey="code" columns={runProductColumns} dataSource={r.products} size="small" pagination={false} />
            ),
            rowExpandable: (r: FormazioneRun) => r.products.length > 0,
          }}
        />
      </Card>
    </div>
  );
};

export default Formazione;
